import React, { useContext } from 'react';
import * as PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';

import { storeContext } from '../../../../context';
import { paymentTypes } from '../../../../../constants/mixpanel';
import PopoverButton from './PopoverButton';
import { t } from '../../../../../utils/i18next';

const translationsBase = 'payables.payment_options';

const PaymentPlanButtonOptions = observer(props => {
  const { payable, settings } = useContext(storeContext);
  const {
    paymentPlan,
    billIdsNotOnPaymentPlan,
    isPayzenPaymentPlan,
    selectedAppointments,
    selectedAppointmentsRemainingBalance,
    isLoadingFullOffers
  } = payable;
  const { buttonText, buttonColor } = props;
  const {
    PP_BRING_CURRENT,
    ADD_OUTSIDE_BALANCE,
    PAYMENT,
    PAY_FULL_PLAN,
    INSIDE_PP,
    OUTSIDE_PP,
    INSIDE_FINANCING_PP,
    OUTSIDE_FINANCING_PP
  } = paymentTypes;

  const isOneTimePaymentSeparated = settings.get(
    'DEF_562_PE_separate_one_time_payment_user_activities'
  );

  const getPaymentPlanOptions = () => {
    const amountToPay = payable.getTotalAmountByPaymentType(PAYMENT);
    const fullAmount = payable.getTotalAmountByPaymentType(PAY_FULL_PLAN);

    const getAddBalanceToPlanSubText = () => {
      if (!selectedAppointments.length) {
        return {};
      }
      if (!billIdsNotOnPaymentPlan.length) {
        return {
          subText: t('payables.general.appointments_with_count', {
            count: selectedAppointments.length
          })
        };
      }
      return {
        subText: t(`${translationsBase}.add_balance_to_plan_note`, {
          appointments: selectedAppointments.length,
          bills: billIdsNotOnPaymentPlan.length
        })
      };
    };

    const allOptions = {
      bringAccountCurrent: {
        text: `${translationsBase}.bring_account_current`,
        amount: payable.getTotalAmountByPaymentType(PP_BRING_CURRENT),
        paymentButtonType: PP_BRING_CURRENT,
        noteSelectedBills: false
      },
      addBalanceToPlan: {
        text: `${translationsBase}.add_balance_to_plan`,
        ...getAddBalanceToPlanSubText(),
        amount:
          paymentPlan.amountOutOfPlan + selectedAppointmentsRemainingBalance,
        paymentButtonType: ADD_OUTSIDE_BALANCE,
        noteSelectedBills: payable.selectableNextPayment > 0,
        disabled: isLoadingFullOffers
      },
      payNow: {
        text: `${translationsBase}.pay_now`,
        amount: amountToPay,
        paymentButtonType: PAYMENT,
        noteSelectedBills: payable.selectableNextPayment > 0
      },
      payFullPlan: {
        text:
          amountToPay > 0
            ? `${translationsBase}.pay_full`
            : `${translationsBase}.pay_now`,
        amount: fullAmount,
        paymentButtonType: PAY_FULL_PLAN,
        noteSelectedBills: false
      },
      payTowardPlan: {
        text: `${translationsBase}.pay_toward_payment_plan`,
        amount: payable.getTotalAmountByPaymentType(INSIDE_PP),
        paymentButtonType: INSIDE_PP
      },
      payOutsidePlan: {
        text: `${translationsBase}.pay_outside_payment_plan`,
        amount: payable.getTotalAmountByPaymentType(OUTSIDE_PP),
        paymentButtonType: OUTSIDE_PP
      },
      payTowardFinancedPlan: {
        text: `${translationsBase}.pay_toward_payment_plan`,
        amount: payable.getTotalAmountByPaymentType(INSIDE_FINANCING_PP),
        paymentButtonType: INSIDE_FINANCING_PP,
        noteSelectedBills: false
      },
      payOutsideFinancedPlan: {
        text: `${translationsBase}.pay_outside_payment_plan`,
        amount: payable.billsNotOnPaymentPlanAmount,
        paymentButtonType: OUTSIDE_FINANCING_PP,
        noteSelectedBills: false
      },
      addBillToExistingFinancedPlan: {
        text: `${translationsBase}.add_bill_to_existing_payment_plan`,
        amount: payable.billsNotOnPaymentPlanAmount,
        paymentButtonType: ADD_OUTSIDE_BALANCE,
        noteSelectedBills: false
      }
    };
    const financedPlanOptions = [
      allOptions.payTowardFinancedPlan,
      ...((payable.getTotalAmountByPaymentType(OUTSIDE_FINANCING_PP) > 0 && [
        allOptions.payOutsideFinancedPlan
      ]) ||
        []),
      ...((payable.billIdsNotOnPaymentPlan.length > 0 &&
        paymentPlan.amountOutOfPlan > 0 && [
          allOptions.addBillToExistingFinancedPlan
        ]) ||
        [])
    ];
    const paymentPlanOptions = [
      ...((paymentPlan.isDelinquent && [allOptions.bringAccountCurrent]) || []),
      ...((paymentPlan.amountOutOfPlan + selectedAppointmentsRemainingBalance >
        0 && [allOptions.addBalanceToPlan]) ||
        []),
      ...((amountToPay > 0 && [allOptions.payNow]) || []),
      ...((fullAmount > 0 && [allOptions.payFullPlan]) || [])
    ];
    const seperatedPaymentPlanOptions = [
      ...((paymentPlan.isDelinquent && [allOptions.bringAccountCurrent]) || []),
      ...((paymentPlan.amountOutOfPlan > 0 && [allOptions.addBalanceToPlan]) ||
        []),
      allOptions.payTowardPlan,
      ...((paymentPlan.amountOutOfPlan > 0 && [allOptions.payOutsidePlan]) ||
        []),
      ...((fullAmount > 0 && [allOptions.payFullPlan]) || [])
    ];
    if (isPayzenPaymentPlan) return financedPlanOptions;
    if (isOneTimePaymentSeparated) return seperatedPaymentPlanOptions;
    return paymentPlanOptions;
  };

  return (
    <div
      data-testid="paymentPlanButtonOptions"
      className="paymentPlanButtonOptions"
      style={{ width: '100%' }}
    >
      <PopoverButton
        buttonText={buttonText}
        buttonColor={buttonColor}
        paymentOptions={getPaymentPlanOptions()}
      />
    </div>
  );
});

PaymentPlanButtonOptions.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonColor: PropTypes.string.isRequired
};

export default PaymentPlanButtonOptions;
